import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { X, Save, MessageSquare, HelpCircle, Image as ImageIcon, CheckCircle, CreditCard, Bell, Timer, Layout, Send, Upload } from 'lucide-react';
import { Toast } from '../Toast';

interface BotData {
    id: string;
    name: string;
    welcome_message?: string;
    support_text?: string;
    welcome_media?: string;
    linked_plans?: string; // JSON Array of IDs
    enable_support?: boolean;
    spam_settings?: any;
}

interface SpamGatilho {
    enabled: boolean;
    delay: number;
    message: string;
    media_url: string;
}

interface SpamSettings {
    recovery_pix: SpamGatilho;
    recovery_start: SpamGatilho;
}

interface MediaFile {
    name: string;
    url: string;
    type: 'image' | 'video';
}

interface Plan {
    id: string;
    name: string;
    price: number;
}

interface BotEditModalProps {
    bot: BotData;
    onClose: () => void;
    onSuccess: () => void;
}

export const BotEditModal: React.FC<BotEditModalProps> = ({ bot, onClose, onSuccess }) => {
    const [welcomeMessage, setWelcomeMessage] = useState(bot.welcome_message || '');
    const [supportText, setSupportText] = useState(bot.support_text || '');
    const [enableSupport, setEnableSupport] = useState(bot.enable_support !== false); // Default True if undefined
    const [selectedMedia, setSelectedMedia] = useState(bot.welcome_media || '');

    // Parse initial linked plans (pode vir como string JSON ou já como array do Supabase/jsonb)
    const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>(() => {
        const raw = bot.linked_plans;
        if (raw == null || raw === '') return [];
        if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
        try {
            const parsed = JSON.parse(String(raw));
            return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
        } catch {
            return [];
        }
    });

    const [mediaList, setMediaList] = useState<MediaFile[]>([]);
    const [planList, setPlanList] = useState<Plan[]>([]); // List of available plans
    const [loading, setLoading] = useState(false);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeTab, setActiveTab] = useState<'general' | 'recovery'>('general');

    const defaultSpamSettings: SpamSettings = {
        recovery_pix: { enabled: false, delay: 30, message: 'Olá! Notei que você gerou um Pix mas ainda não concluiu o pagamento. Precisa de ajuda?', media_url: '' },
        recovery_start: { enabled: false, delay: 60, message: 'Olá! Você iniciou o bot mas ainda não escolheu um plano. Temos ofertas especiais para você!', media_url: '' }
    };

    const [spamSettings, setSpamSettings] = useState<SpamSettings>(() => {
        let raw = bot.spam_settings;
        if (raw == null) return defaultSpamSettings;
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (!parsed || typeof parsed !== 'object') return defaultSpamSettings;
            return {
                recovery_pix: { ...defaultSpamSettings.recovery_pix, ...(parsed.recovery_pix || {}) },
                recovery_start: { ...defaultSpamSettings.recovery_start, ...(parsed.recovery_start || {}) }
            };
        } catch {
            return defaultSpamSettings;
        }
    });

    useEffect(() => {
        fetchMedia();
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const res = await api.get('/plans');
            setPlanList(res.data);
        } catch (e) {
            console.error("Erro loading plans", e);
        }
    };

    const fetchMedia = async () => {
        setLoadingMedia(true);
        try {
            const res = await api.get('/media');
            setMediaList(res.data);
        } catch (e) {
            console.error("Erro loading media", e);
        } finally {
            setLoadingMedia(false);
        }
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            await api.post('/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setToast({ msg: 'Mídia enviada!', type: 'success' });
            fetchMedia();
        } catch (err: any) {
            setToast({ msg: err.response?.data?.error || 'Erro no upload.', type: 'error' });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            await api.put(`/bots/${bot.id}`, {
                welcome_message: welcomeMessage,
                support_text: supportText,
                welcome_media: selectedMedia,
                linked_plans: JSON.stringify(selectedPlanIds),
                enable_support: enableSupport,
                spam_settings: spamSettings
            });
            onSuccess();
            onClose();
        } catch (e: any) {
            setToast({ msg: "Erro ao salvar alterações.", type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bot-edit-modal" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
            <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleUpload} style={{ display: 'none' }} />

            <div className="bot-edit-modal-inner modal-content-premium" style={{
                background: '#161618',
                borderRadius: '20px',
                width: '900px', // Mais largo para acomodar 2 colunas
                maxWidth: '95%',
                border: '1px solid #333',
                color: 'white',
                boxShadow: '0 40px 80px rgba(0, 0, 0, 0.6)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '90vh' // Garante que não estoure a vertical
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 30px',
                    borderBottom: '1px solid #282828',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#1a1a1c'
                }}>
                    <div>
                        <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Editar Bot: <span style={{ color: '#ff6a00' }}>{bot.name}</span></h2>
                    </div>
                    <button onClick={onClose} style={{
                        background: '#222', border: 'none', color: '#fff',
                        cursor: 'pointer', padding: '6px', borderRadius: '50%'
                    }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Tabs Header */}
                <div style={{
                    display: 'flex',
                    background: '#1a1a1c',
                    padding: '0 30px',
                    borderBottom: '1px solid #282828'
                }}>
                    <div
                        onClick={() => setActiveTab('general')}
                        style={{
                            padding: '12px 20px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: activeTab === 'general' ? '#ff6a00' : '#666',
                            borderBottom: activeTab === 'general' ? '2px solid #ff6a00' : '2px solid transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: '0.2s'
                        }}
                    >
                        <Layout size={14} /> GERAL
                    </div>
                    <div
                        onClick={() => setActiveTab('recovery')}
                        style={{
                            padding: '12px 20px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: activeTab === 'recovery' ? '#ff6a00' : '#666',
                            borderBottom: activeTab === 'recovery' ? '2px solid #ff6a00' : '2px solid transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: '0.2s'
                        }}
                    >
                        <Bell size={14} /> RECUPERAÇÃO / SPAM
                    </div>
                </div>

                {/* Body - GRID LAYOUT */}
                <div className="bot-edit-general" style={{
                    flex: 1,
                    display: activeTab === 'general' ? 'grid' : 'none',
                    gridTemplateColumns: '1fr 340px',
                    overflow: 'hidden'
                }}>
                    {/* ... (Existing Content) ... */}
                    <div style={{ padding: '30px', overflowY: 'auto', borderRight: '1px solid #282828' }}>
                        {/* Welcome Message */}
                        <div style={{ marginBottom: '25px', display: 'flex', flexDirection: 'column', height: 'calc(35% - 15px)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <MessageSquare size={16} color="#ff6a00" />
                                <label style={{ fontSize: '13px', fontWeight: 600, color: '#ccc', letterSpacing: '0.5px' }}>BOAS-VINDAS (/start)</label>
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <textarea
                                    style={{
                                        flex: 1, width: '100%',
                                        background: '#0a0a0a', border: '1px solid #333', borderRadius: '12px',
                                        padding: '16px', color: '#fff', fontSize: '14px', lineHeight: '1.5',
                                        outline: 'none', resize: 'none', fontFamily: 'inherit',
                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)'
                                    }}
                                    placeholder="Escreva a mensagem que seu bot enviará ao iniciar..."
                                    value={welcomeMessage}
                                    onChange={e => setWelcomeMessage(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Support Text */}
                        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(35% - 15px)', marginBottom: '25px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <HelpCircle size={16} color="#facc15" />
                                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#ccc', letterSpacing: '0.5px' }}>SUPORTE / AJUDA</label>
                                </div>
                                <div
                                    onClick={() => setEnableSupport(!enableSupport)}
                                    style={{
                                        width: '36px', height: '20px',
                                        background: enableSupport ? '#4ade80' : '#444',
                                        borderRadius: '20px', position: 'relative', cursor: 'pointer',
                                        transition: '0.2s'
                                    }}
                                >
                                    <div style={{
                                        width: '16px', height: '16px', background: '#fff', borderRadius: '50%',
                                        position: 'absolute', top: '2px',
                                        left: enableSupport ? '18px' : '2px',
                                        transition: '0.2s'
                                    }} />
                                </div>
                            </div>

                            {enableSupport ? (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.2s' }}>
                                    <textarea
                                        style={{
                                            flex: 1, width: '100%',
                                            background: '#0a0a0a', border: '1px solid #333', borderRadius: '12px',
                                            padding: '16px', color: '#fff', fontSize: '14px', lineHeight: '1.5',
                                            outline: 'none', resize: 'none', fontFamily: 'inherit',
                                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)'
                                        }}
                                        placeholder="Mensagem exibida ao clicar no botão de suporte..."
                                        value={supportText}
                                        onChange={e => setSupportText(e.target.value)}
                                    />
                                </div>
                            ) : (
                                <div style={{
                                    flex: 1, border: '1px dashed #333', borderRadius: '12px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#555', fontSize: '13px'
                                }}>
                                    Botão de suporte desativado.
                                </div>
                            )}
                        </div>

                        {/* Linked Plans */}
                        <div style={{ display: 'flex', flexDirection: 'column', height: '30%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <CreditCard size={16} color="#4ade80" />
                                <label style={{ fontSize: '13px', fontWeight: 600, color: '#ccc', letterSpacing: '0.5px' }}>PLANOS VINCULADOS</label>
                            </div>
                            <div style={{ flex: 1, background: '#0a0a0a', border: '1px solid #333', borderRadius: '12px', padding: '10px', overflowY: 'auto' }}>
                                {planList.length === 0 ? (
                                    <div style={{ color: '#666', fontSize: '13px', textAlign: 'center', marginTop: '20px' }}>Nenhum plano disponível.<br />Crie planos na aba "Planos".</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {planList.map(plan => {
                                            const isSelected = selectedPlanIds.includes(plan.id);
                                            return (
                                                <div
                                                    key={plan.id}
                                                    onClick={() => {
                                                        if (isSelected) setSelectedPlanIds(selectedPlanIds.filter(id => id !== plan.id));
                                                        else setSelectedPlanIds([...selectedPlanIds, plan.id]);
                                                    }}
                                                    style={{
                                                        background: isSelected ? 'rgba(74, 222, 128, 0.1)' : '#161618',
                                                        border: isSelected ? '1px solid #4ade80' : '1px solid #333',
                                                        padding: '10px', borderRadius: '8px',
                                                        cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                                    }}
                                                >
                                                    <span style={{ fontSize: '13px', color: isSelected ? '#fff' : '#aaa' }}>{plan.name}</span>
                                                    <span style={{ fontSize: '12px', color: '#4ade80', fontWeight: 600 }}>R$ {plan.price}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ padding: '24px', background: '#121214', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                            <ImageIcon size={16} color="#a78bfa" />
                            <label style={{ fontSize: '13px', fontWeight: 600, color: '#ccc', letterSpacing: '0.5px' }}>MÍDIA ANEXADA</label>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                <div onClick={() => setSelectedMedia('')} style={{ aspectRatio: '1', borderRadius: '12px', border: selectedMedia === '' ? '2px solid #ff6a00' : '1px dashed #444', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: selectedMedia === '' ? 'rgba(0, 229, 255, 0.05)' : '#1a1a1a' }}>
                                    <X size={20} color={selectedMedia === '' ? '#ff6a00' : '#444'} />
                                    <span style={{ fontSize: '10px', color: selectedMedia === '' ? '#ff6a00' : '#666', marginTop: '6px' }}>Nenhuma</span>
                                </div>
                                <div onClick={() => !uploading && fileInputRef.current?.click()} style={{ aspectRatio: '1', borderRadius: '12px', border: '1px dashed #ff6a00', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', background: 'rgba(255, 106, 0, 0.05)' }}>
                                    <Upload size={20} color="#ff6a00" />
                                    <span style={{ fontSize: '10px', color: '#ff6a00', marginTop: '6px' }}>{uploading ? 'Enviando...' : 'Upload'}</span>
                                </div>
                                {mediaList.map(media => (
                                    <div key={media.name} onClick={() => setSelectedMedia(media.url)} style={{ position: 'relative', aspectRatio: '1', borderRadius: '12px', border: selectedMedia === media.url ? '2px solid #ff6a00' : '1px solid #333', overflow: 'hidden', cursor: 'pointer' }}>
                                        {media.type === 'image' ? <img src={media.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '9px' }}>VIDEO</div>}
                                        {selectedMedia === media.url && <div style={{ position: 'absolute', top: 6, right: 6, background: '#ff6a00', borderRadius: '50%', padding: '2px' }}><CheckCircle size={12} color="#000" /></div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body - RECOVERY TAB */}
                <div className="bot-edit-recovery" style={{
                    flex: 1,
                    display: activeTab === 'recovery' ? 'flex' : 'none',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    background: '#0a0a0a'
                }}>
                    <div className="bot-edit-recovery-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: '#222', flex: 1 }}>

                        {/* RECOVERY ITEM 1: PIX ABANDONADO */}
                        <div style={{ padding: '30px', background: '#161618', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: 'rgba(0, 229, 255, 0.1)', padding: '8px', borderRadius: '10px' }}>
                                        <CreditCard size={20} color="#ff6a00" />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '15px', color: '#fff' }}>Pagamento Pendente</h3>
                                        <p style={{ margin: 0, fontSize: '11px', color: '#666' }}>Gerou Pix mas não pagou</p>
                                    </div>
                                </div>
                                <div
                                    onClick={() => setSpamSettings({ ...spamSettings, recovery_pix: { ...spamSettings.recovery_pix, enabled: !spamSettings.recovery_pix.enabled } })}
                                    style={{
                                        width: '44px', height: '24px', background: spamSettings.recovery_pix.enabled ? '#ff6a00' : '#333',
                                        borderRadius: '12px', position: 'relative', cursor: 'pointer', transition: '0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '20px', height: '20px', background: '#fff', borderRadius: '50%',
                                        position: 'absolute', top: '2px', left: spamSettings.recovery_pix.enabled ? '22px' : '2px', transition: '0.3s'
                                    }} />
                                </div>
                            </div>

                            <div style={{ opacity: spamSettings.recovery_pix.enabled ? 1 : 0.4, pointerEvents: spamSettings.recovery_pix.enabled ? 'auto' : 'none', transition: '0.3s' }}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ fontSize: '12px', color: '#999', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <MessageSquare size={14} /> Mensagem de Spam
                                    </label>
                                    <textarea
                                        value={spamSettings.recovery_pix.message}
                                        onChange={(e) => setSpamSettings({ ...spamSettings, recovery_pix: { ...spamSettings.recovery_pix, message: e.target.value } })}
                                        style={{
                                            width: '100%', height: '100px', background: '#0a0a0a', border: '1px solid #333',
                                            borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '13px', outline: 'none', resize: 'none'
                                        }}
                                        placeholder="Ex: Olá! Notamos que seu pagamento PIX está pendente..."
                                    />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ fontSize: '12px', color: '#999', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Timer size={14} /> Enviar após (minutos)
                                    </label>
                                    <input
                                        type="number"
                                        value={spamSettings.recovery_pix.delay}
                                        onChange={(e) => setSpamSettings({ ...spamSettings, recovery_pix: { ...spamSettings.recovery_pix, delay: parseInt(e.target.value) } })}
                                        style={{
                                            width: '100%', background: '#0a0a0a', border: '1px solid #333',
                                            borderRadius: '8px', padding: '10px', color: '#fff', fontSize: '13px', outline: 'none'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '12px', color: '#999', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <ImageIcon size={14} /> Mídia Opcional
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                                        <div onClick={() => setSpamSettings({ ...spamSettings, recovery_pix: { ...spamSettings.recovery_pix, media_url: '' } })} style={{ aspectRatio: '1', borderRadius: '6px', border: spamSettings.recovery_pix.media_url === '' ? '2px solid #ff6a00' : '1px dashed #333', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                            <X size={14} color={spamSettings.recovery_pix.media_url === '' ? '#ff6a00' : '#444'} />
                                        </div>
                                        <div onClick={() => !uploading && fileInputRef.current?.click()} style={{ aspectRatio: '1', borderRadius: '6px', border: '1px dashed #ff6a00', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', background: 'rgba(255, 106, 0, 0.05)' }}>
                                            <Upload size={12} color="#ff6a00" />
                                        </div>
                                        {mediaList.slice(0, 2).map(m => (
                                            <div key={m.name} onClick={() => setSpamSettings({ ...spamSettings, recovery_pix: { ...spamSettings.recovery_pix, media_url: m.url } })} style={{ aspectRatio: '1', borderRadius: '6px', border: spamSettings.recovery_pix.media_url === m.url ? '2px solid #ff6a00' : '1px solid #333', overflow: 'hidden', cursor: 'pointer' }}>
                                                <img src={m.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RECOVERY ITEM 2: START SEM ESCOLHA */}
                        <div style={{ padding: '30px', background: '#161618', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: 'rgba(250, 204, 21, 0.1)', padding: '8px', borderRadius: '10px' }}>
                                        <Send size={20} color="#facc15" />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '15px', color: '#fff' }}>Abandono de Início</h3>
                                        <p style={{ margin: 0, fontSize: '11px', color: '#666' }}>Deu /start mas não escolheu plano</p>
                                    </div>
                                </div>
                                <div
                                    onClick={() => setSpamSettings({ ...spamSettings, recovery_start: { ...spamSettings.recovery_start, enabled: !spamSettings.recovery_start.enabled } })}
                                    style={{
                                        width: '44px', height: '24px', background: spamSettings.recovery_start.enabled ? '#facc15' : '#333',
                                        borderRadius: '12px', position: 'relative', cursor: 'pointer', transition: '0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '20px', height: '20px', background: '#fff', borderRadius: '50%',
                                        position: 'absolute', top: '2px', left: spamSettings.recovery_start.enabled ? '22px' : '2px', transition: '0.3s'
                                    }} />
                                </div>
                            </div>

                            <div style={{ opacity: spamSettings.recovery_start.enabled ? 1 : 0.4, pointerEvents: spamSettings.recovery_start.enabled ? 'auto' : 'none', transition: '0.3s' }}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ fontSize: '12px', color: '#999', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <MessageSquare size={14} /> Mensagem de Spam
                                    </label>
                                    <textarea
                                        value={spamSettings.recovery_start.message}
                                        onChange={(e) => setSpamSettings({ ...spamSettings, recovery_start: { ...spamSettings.recovery_start, message: e.target.value } })}
                                        style={{
                                            width: '100%', height: '100px', background: '#0a0a0a', border: '1px solid #333',
                                            borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '13px', outline: 'none', resize: 'none'
                                        }}
                                        placeholder="Ex: Ei! Vi que você ainda não aproveitou nossos planos VIP..."
                                    />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ fontSize: '12px', color: '#999', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Timer size={14} /> Enviar após (minutos)
                                    </label>
                                    <input
                                        type="number"
                                        value={spamSettings.recovery_start.delay}
                                        onChange={(e) => setSpamSettings({ ...spamSettings, recovery_start: { ...spamSettings.recovery_start, delay: parseInt(e.target.value) } })}
                                        style={{
                                            width: '100%', background: '#0a0a0a', border: '1px solid #333',
                                            borderRadius: '8px', padding: '10px', color: '#fff', fontSize: '13px', outline: 'none'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '12px', color: '#999', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <ImageIcon size={14} /> Mídia Opcional
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                                        <div onClick={() => setSpamSettings({ ...spamSettings, recovery_start: { ...spamSettings.recovery_start, media_url: '' } })} style={{ aspectRatio: '1', borderRadius: '6px', border: spamSettings.recovery_start.media_url === '' ? '2px solid #facc15' : '1px dashed #333', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                            <X size={14} color={spamSettings.recovery_start.media_url === '' ? '#facc15' : '#444'} />
                                        </div>
                                        <div onClick={() => !uploading && fileInputRef.current?.click()} style={{ aspectRatio: '1', borderRadius: '6px', border: '1px dashed #facc15', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', background: 'rgba(250, 204, 21, 0.05)' }}>
                                            <Upload size={12} color="#facc15" />
                                        </div>
                                        {mediaList.slice(0, 2).map(m => (
                                            <div key={m.name} onClick={() => setSpamSettings({ ...spamSettings, recovery_start: { ...spamSettings.recovery_start, media_url: m.url } })} style={{ aspectRatio: '1', borderRadius: '6px', border: spamSettings.recovery_start.media_url === m.url ? '2px solid #facc15' : '1px solid #333', overflow: 'hidden', cursor: 'pointer' }}>
                                                <img src={m.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div style={{ padding: '15px 30px', background: '#111', borderTop: '1px solid #282828', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Bell size={14} color="#ff6a00" />
                        <span style={{ fontSize: '12px', color: '#888' }}>
                            O sistema enviará a mensagem automaticamente se o usuário não converter no tempo definido.
                        </span>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '20px 30px',
                    borderTop: '1px solid #282828',
                    background: '#1a1a1c',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px'
                }}>
                    <button onClick={onClose} style={{
                        background: 'transparent', border: '1px solid #444', color: '#aaa',
                        padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 500,
                        fontSize: '13px'
                    }}>
                        Cancelar
                    </button>
                    <button onClick={handleSave} disabled={loading} style={{
                        background: 'var(--btn-gradient)',
                        border: 'none', color: '#000',
                        padding: '10px 32px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600,
                        fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
                        boxShadow: 'none'
                    }}>
                        {loading ? 'Salvando...' : <><Save size={16} /> Salvar Alterações</>}
                    </button>
                </div>
            </div>
        </div>
    );
};
